import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
} from "react";
import { api, fetchMe, loginRequest } from "./api";

const STORAGE_KEY = "valax_admin_token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() =>
        localStorage.getItem(STORAGE_KEY),
    );
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("loading"); // "loading" | "authed" | "guest"

    // Attach token to every request; handle 401 by auto-logout
    useEffect(() => {
        const reqId = api.interceptors.request.use((config) => {
            const t = localStorage.getItem(STORAGE_KEY);
            if (t && config.headers) {
                config.headers.Authorization = `Bearer ${t}`;
            }
            return config;
        });
        const respId = api.interceptors.response.use(
            (r) => r,
            (error) => {
                if (error?.response?.status === 401 && localStorage.getItem(STORAGE_KEY)) {
                    // Token invalid/expired — clear and surface to the user
                    localStorage.removeItem(STORAGE_KEY);
                    setToken(null);
                    setUser(null);
                    setStatus("guest");
                }
                return Promise.reject(error);
            },
        );
        return () => {
            api.interceptors.request.eject(reqId);
            api.interceptors.response.eject(respId);
        };
    }, []);

    // Verify session on mount / when token changes
    useEffect(() => {
        let cancelled = false;
        if (!token) {
            setUser(null);
            setStatus("guest");
            return;
        }
        (async () => {
            try {
                const me = await fetchMe();
                if (!cancelled) {
                    setUser(me);
                    setStatus("authed");
                }
            } catch {
                if (!cancelled) {
                    localStorage.removeItem(STORAGE_KEY);
                    setToken(null);
                    setUser(null);
                    setStatus("guest");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const login = useCallback(async (email, password) => {
        const data = await loginRequest(email, password);
        localStorage.setItem(STORAGE_KEY, data.token);
        setToken(data.token);
        setUser(data.user);
        setStatus("authed");
        return data;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
        setUser(null);
        setStatus("guest");
    }, []);

    return (
        <AuthContext.Provider
            value={{ user, token, status, login, logout }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
